import { useCallback } from "react";
import { useAppDispatch } from "./redux";
import { fetchUserDetails } from "../store/slices/userSlice";
import { performLogout } from "../utils/logoutSession";
import { fetchCategories } from "../store/slices/categorySlice";
import {
  fetchConditions,
  fetchReviewCategories,
} from "../store/slices/conditionSlice";
import { ErrorToaster, SuccessToaster } from "../helpers/toasterHelper";
import { ERROR_MESSAGES, SUCCESS_MESSAGES, USER_STATUS_FLOW } from "../utils/constants";
import { DASHBOARD_PATH } from "../constants";
import {
  consumePendingDeepLink,
  getRedirectPathFromLoginSearch,
  isProtectedDeepLinkPath,
} from "../utils/deepLink";
import { isUserOnboardingRequired } from "../utils/userOnboarding";
import { isDevelopmentEnv } from "../utils/formatters";

/** Full navigation so App router rebuilds with protected routes (avoids stale / → login redirect). */
function redirectAfterAuth(path: string): void {
  window.location.replace(path);
}

export type CompleteAuthenticatedSessionOptions = {
  showSuccessToast?: boolean;
  successMessage?: string;
};

export type CompleteAuthenticatedSessionResult =
  | { status: typeof USER_STATUS_FLOW.COMPLETED }
  | { status: typeof USER_STATUS_FLOW.ONBOARDING_REQUIRED; userId: string }
  | { status: typeof USER_STATUS_FLOW.FAILED };

export function useCompleteAuthenticatedSession() {
  const dispatch = useAppDispatch();

  const loadAppDataAndRedirect = useCallback(async () => {
    const pendingFromStorage = consumePendingDeepLink();
    const pendingFromQuery =
      typeof window !== "undefined"
        ? getRedirectPathFromLoginSearch(window.location.search)
        : null;
    const pendingDeepLink = pendingFromStorage || pendingFromQuery;

    await Promise.all([
      dispatch(fetchCategories()),
      dispatch(fetchConditions()),
      dispatch(fetchReviewCategories()),
    ]);

    redirectAfterAuth(
      pendingDeepLink && isProtectedDeepLinkPath(pendingDeepLink)
        ? pendingDeepLink
        : DASHBOARD_PATH
    );
  }, [dispatch]);

  const completeAuthenticatedSession = useCallback(
    async (
      userId: string,
      options: CompleteAuthenticatedSessionOptions = {}
    ): Promise<CompleteAuthenticatedSessionResult> => {
      const {
        showSuccessToast = true,
        successMessage = SUCCESS_MESSAGES.SUCCESSFULLY_LOGGED_IN,
      } = options;

      if (showSuccessToast) {
        SuccessToaster(successMessage);
      }

      const userDetailsResult = await dispatch(fetchUserDetails({ userId }));
      if (fetchUserDetails.rejected.match(userDetailsResult)) {
        const errorMessage =
          (userDetailsResult.payload as { message?: string })?.message ||
          ERROR_MESSAGES.FAILED_TO_FETCH_USER_DETAILS;
        ErrorToaster(errorMessage);
        if (!isDevelopmentEnv()) {
          await performLogout(dispatch);
        }
        return { status: USER_STATUS_FLOW.FAILED };
      }

      const user = userDetailsResult.payload?.user;

      if (isUserOnboardingRequired(user)) {
        return { status: USER_STATUS_FLOW.ONBOARDING_REQUIRED, userId };
      }

      const isBlockedByAdmin = Boolean(user?.blockedByAdmin);

      if (isBlockedByAdmin) {
        redirectAfterAuth(DASHBOARD_PATH);
        return { status: USER_STATUS_FLOW.COMPLETED };
      }

      await loadAppDataAndRedirect();
      return { status: USER_STATUS_FLOW.COMPLETED };
    },
    [dispatch, loadAppDataAndRedirect]
  );

  const continueSessionAfterOnboarding = useCallback(
    async (userId: string): Promise<CompleteAuthenticatedSessionResult> => {
      const userDetailsResult = await dispatch(fetchUserDetails({ userId }));
      if (fetchUserDetails.rejected.match(userDetailsResult)) {
        const errorMessage =
          (userDetailsResult.payload as { message?: string })?.message ||
          ERROR_MESSAGES.FAILED_TO_FETCH_USER_DETAILS;
        ErrorToaster(errorMessage);
        if (!isDevelopmentEnv()) {
          await performLogout(dispatch);
        }
        return { status: USER_STATUS_FLOW.FAILED };
      }

      SuccessToaster(SUCCESS_MESSAGES.ONBOARDING_COMPLETED_SUCCESSFULLY);
      await loadAppDataAndRedirect();
      return { status: USER_STATUS_FLOW.COMPLETED };
    },
    [dispatch, loadAppDataAndRedirect]
  );

  return {
    completeAuthenticatedSession,
    continueSessionAfterOnboarding,
  };
}
